import { FormEvent, useEffect } from "react"
import { useForm, usePage } from "@inertiajs/react"
import { SeoHead } from "@/components/SeoHead"
import { MarketingLayout } from "@/components/marketing/MarketingLayout"
import { Section, Eyebrow } from "@/components/marketing/sections/Section"
import { Input } from "@/components/ui/input"
import { Button } from "@/components/ui/button"
import { Radio } from "@/components/ui/radio"
import { track } from "@/lib/analytics"
import type { PageProps } from "@/types/inertia"

type Question = {
  key: string
  prompt: string
  options: { key: string; label: string }[]
}

type Props = {
  questions: Question[]
}

const DESCRIPTION =
  "A free 90-second scorecard for founders, coaches, and experts running a show. Find out what's actually driving — or stalling — your podcast's compound."

const WHAT_YOU_GET = [
  {
    title: "An honest grade",
    line: "A–F on six dimensions that decide whether your show is a content checkbox or a relationship engine.",
  },
  {
    title: "Specific commentary",
    line: "Not just a number — a short read on every answer, in plain language. What's working, what's leaking, what to do about it.",
  },
  {
    title: "A next step (or not)",
    line: "If your show is already compounding, you'll see it on the page. If it isn't, you'll know exactly where to look first.",
  },
]

export default function PodcastScorecard({ questions }: Props) {
  const { props } = usePage<PageProps>()
  const errors = props.errors ?? {}

  const initialAnswers = Object.fromEntries(questions.map((q) => [q.key, ""]))

  const form = useForm({
    email: "",
    name: "",
    podcast_name: "",
    podcast_url: "",
    answers: initialAnswers as Record<string, string>,
    nickname: "",
    referrer: "",
    utm_source: "",
    utm_medium: "",
    utm_campaign: "",
  })

  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    form.setData((data) => ({
      ...data,
      referrer: document.referrer || "",
      utm_source: params.get("utm_source") || "",
      utm_medium: params.get("utm_medium") || "",
      utm_campaign: params.get("utm_campaign") || "",
    }))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const setAnswer = (qkey: string, answerKey: string) => {
    form.setData("answers", { ...form.data.answers, [qkey]: answerKey })
  }

  const submit = (e: FormEvent) => {
    e.preventDefault()
    track("Podcast Scorecard Submitted")
    form.post("/podcast-scorecard")
  }

  return (
    <MarketingLayout>
      <SeoHead title="Podcast Strategy Scorecard" description={DESCRIPTION} noindex />

      {/* Hero */}
      <Section containerClassName="max-w-3xl">
        <Eyebrow>FullCast · Podcast Growth Partnership</Eyebrow>
        <h1 className="display-1 mt-4">Is your podcast actually growing your business?</h1>
        <p className="mt-5 text-lg text-ink-body">
          A 90-second scorecard for founders, coaches, and experts running a show. Six
          questions. An honest grade. Plain-language commentary on every answer.
        </p>
      </Section>

      {/* What you get */}
      <Section className="bg-surface" containerClassName="max-w-4xl">
        <Eyebrow>What you'll see</Eyebrow>
        <h2 className="display-2 mt-4">A diagnostic, not a download</h2>
        <div className="mt-8 grid gap-5 sm:grid-cols-3">
          {WHAT_YOU_GET.map((item) => (
            <div key={item.title} className="rounded-xl border border-hairline bg-page p-6">
              <h3>{item.title}</h3>
              <p className="mt-2 text-ink-muted">{item.line}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* Form */}
      <Section containerClassName="max-w-3xl">
        <form onSubmit={submit} className="space-y-10" noValidate>
          {/* Honeypot */}
          <div aria-hidden="true" className="absolute left-[-9999px] top-[-9999px] h-0 w-0 overflow-hidden">
            <label htmlFor="nickname">Leave this field empty</label>
            <input
              id="nickname"
              type="text"
              tabIndex={-1}
              autoComplete="off"
              value={form.data.nickname}
              onChange={(e) => form.setData("nickname", e.target.value)}
            />
          </div>

          {/* Question groups */}
          {questions.map((q, i) => (
            <fieldset key={q.key} className="rounded-2xl border border-hairline bg-surface p-6 sm:p-8">
              <p className="text-sm text-ink-muted">Question {i + 1} of {questions.length}</p>
              <h3 className="mt-2">{q.prompt}</h3>
              <div className="mt-5 space-y-3">
                {q.options.map((opt) => {
                  const id = `${q.key}-${opt.key}`
                  return (
                    <label
                      key={opt.key}
                      htmlFor={id}
                      className="flex cursor-pointer items-start gap-3 rounded-lg border border-hairline bg-page p-4 transition hover:border-ink-muted has-[:checked]:border-accent has-[:checked]:bg-surface"
                    >
                      <Radio
                        id={id}
                        name={q.key}
                        value={opt.key}
                        checked={form.data.answers[q.key] === opt.key}
                        onChange={() => setAnswer(q.key, opt.key)}
                        className="mt-0.5"
                      />
                      <span className="font-normal text-ink-body">{opt.label}</span>
                    </label>
                  )
                })}
              </div>
            </fieldset>
          ))}

          {/* About you */}
          <fieldset className="rounded-2xl border border-hairline bg-surface p-6 sm:p-8">
            <p className="text-sm text-ink-muted">About you</p>
            <h3 className="mt-2">Where should we send the result?</h3>
            <div className="mt-6 grid gap-5 sm:grid-cols-2">
              <div className="space-y-2">
                <label htmlFor="email">Email</label>
                <Input
                  id="email"
                  type="email"
                  autoComplete="email"
                  value={form.data.email}
                  onChange={(e) => form.setData("email", e.target.value)}
                />
                {errors.email && <p className="text-xs text-danger-display">{errors.email}</p>}
              </div>
              <div className="space-y-2">
                <label htmlFor="name">First name (optional)</label>
                <Input
                  id="name"
                  autoComplete="given-name"
                  value={form.data.name}
                  onChange={(e) => form.setData("name", e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <label htmlFor="podcast_name">Podcast name (optional)</label>
                <Input
                  id="podcast_name"
                  value={form.data.podcast_name}
                  onChange={(e) => form.setData("podcast_name", e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <label htmlFor="podcast_url">Podcast URL (optional)</label>
                <Input
                  id="podcast_url"
                  inputMode="url"
                  placeholder="show.example or RSS"
                  value={form.data.podcast_url}
                  onChange={(e) => form.setData("podcast_url", e.target.value)}
                />
              </div>
            </div>
          </fieldset>

          <Button type="submit" size="lg" disabled={form.processing}>
            {form.processing ? "Scoring…" : "Score my podcast"}
          </Button>

          <p className="text-sm text-ink-muted">
            We use your answers to score your show and send you the report. We don't share
            or sell your email. Unsubscribe any time.
          </p>
        </form>
      </Section>
    </MarketingLayout>
  )
}
